import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { DEFAULT_COL_WIDTH, MIN_ROW_HEIGHT } from './fileTypes';
import type { FortuneCell, FortuneSheet } from './types';

// ============================================================================
// Sheet features that ride along the .xlsx round trip (converters.ts): frozen
// panes, hidden sheets, the AutoFilter range, conditional formatting, data
// validation, images and cell comments. Pure, DOM-free mapping functions —
// converters.ts runs inside a Web Worker. Every reader/writer skips what it
// can't map instead of throwing: a malformed rule must never cost the import
// or export of the rest of the workbook.
// ============================================================================

type CellRange = { row: [number, number]; column: [number, number] };
type GridExtent = { rows: number; cols: number };
type FortuneImage = NonNullable<FortuneSheet['images']>[number];

type ExcelColor = { argb?: string; theme?: number; tint?: number; indexed?: number };
/** Resolves an exceljs colour (argb or theme+tint) to '#RRGGBB'; converters.ts binds it to the workbook palette. */
export type ColorResolver = (color: ExcelColor | undefined) => string | undefined;

type ExcelCfRule = {
    type?: string;
    operator?: string;
    priority?: number;
    formulae?: unknown[];
    text?: string;
    rank?: number;
    percent?: boolean;
    bottom?: boolean;
    aboveAverage?: boolean;
    gradient?: boolean;
    cfvo?: { type?: string; value?: number }[];
    color?: ExcelColor | ExcelColor[];
    style?: { font?: { color?: ExcelColor } | null; fill?: { fgColor?: ExcelColor; bgColor?: ExcelColor } | null };
};

// exceljs keeps these on the worksheet but leaves them out of its .d.ts.
type WorksheetInternals = {
    conditionalFormattings?: { ref?: string; rules?: ExcelCfRule[] }[];
    dataValidations?: {
        model: Record<string, ExcelJS.DataValidation | undefined>;
        add: (address: string, validation: ExcelJS.DataValidation) => void;
    };
};
const internals = (ws: ExcelJS.Worksheet): WorksheetInternals => ws as unknown as WorksheetInternals;

const isIndex = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0;

const toCellRange = (raw: unknown): CellRange | undefined => {
    const { row, column } = (raw ?? {}) as { row?: unknown[]; column?: unknown[] };
    if (!Array.isArray(row) || !Array.isArray(column)) return undefined;
    const [r1, r2] = row;
    const [c1, c2] = column;
    if (!isIndex(r1) || !isIndex(r2) || !isIndex(c1) || !isIndex(c2)) return undefined;
    return { row: [Math.min(r1, r2), Math.max(r1, r2)], column: [Math.min(c1, c2), Math.max(c1, c2)] };
};

const encodeRange = ({ row, column }: CellRange): string => {
    const tl = XLSX.utils.encode_cell({ r: row[0], c: column[0] });
    return row[0] === row[1] && column[0] === column[1] ? tl : `${tl}:${XLSX.utils.encode_cell({ r: row[1], c: column[1] })}`;
};

const REF_PART = /^\$?([A-Z]{1,3})?\$?(\d+)?$/i;

/**
 * Parse one A1 range ('B2', 'A1:C9', whole-column 'A:A', whole-row '3:3') and
 * clamp it to the sheet's grid. Fortune can't show anything outside its grid,
 * and a whole-column rule would otherwise walk a million rows on every repaint.
 */
const parseRef = (ref: string, extent: GridExtent): CellRange | undefined => {
    const [a, b = a] = ref.replace(/^.*!/, '').split(':');
    const pa = REF_PART.exec(a ?? '');
    const pb = REF_PART.exec(b ?? '');
    if (!pa || !pb || (!pa[1] && !pa[2]) || (!pb[1] && !pb[2])) return undefined;
    const r1 = pa[2] ? Number(pa[2]) - 1 : 0;
    const c1 = pa[1] ? XLSX.utils.decode_col(pa[1].toUpperCase()) : 0;
    const r2 = pb[2] ? Number(pb[2]) - 1 : extent.rows - 1;
    const c2 = pb[1] ? XLSX.utils.decode_col(pb[1].toUpperCase()) : extent.cols - 1;
    const range: CellRange = {
        row: [Math.min(r1, r2), Math.min(Math.max(r1, r2), extent.rows - 1)],
        column: [Math.min(c1, c2), Math.min(Math.max(c1, c2), extent.cols - 1)],
    };
    return range.row[0] >= 0 && range.column[0] >= 0 && range.row[0] <= range.row[1] && range.column[0] <= range.column[1]
        ? range
        : undefined;
};

const sheetExtent = (sheet: FortuneSheet): GridExtent => ({ rows: sheet.row ?? 0, cols: sheet.column ?? 0 });

const MAX_ROWS = 1_048_576;
const MAX_COLS = 16_384;
/** Excel's whole sheet: the extent to parse a range against when it must not be clamped. */
const FULL_EXTENT: GridExtent = { rows: MAX_ROWS, cols: MAX_COLS };

/** The ranges of a space-separated list of refs ('A1:A10 C1:C10'), clamped to `extent`. */
const parseRefs = (refs: string, extent: GridExtent): CellRange[] =>
    refs
        .split(/\s+/)
        .filter(Boolean)
        .map((ref) => parseRef(ref, extent))
        .filter((r): r is CellRange => !!r);

const sameRange = (a: CellRange, b: CellRange): boolean =>
    a.row[0] === b.row[0] && a.row[1] === b.row[1] && a.column[0] === b.column[0] && a.column[1] === b.column[1];
const sameRanges = (a: CellRange[], b: CellRange[]): boolean => a.length === b.length && a.every((range, i) => sameRange(range, b[i]));
const copyRange = ({ row, column }: CellRange): CellRange => ({ row: [row[0], row[1]], column: [column[0], column[1]] });

/** Non-overlapping rectangles covering exactly `cells`: each row's runs of adjacent columns, a run joined to the
 *  rectangle above it when both span the same columns. */
const rectanglesOf = (cells: [number, number][]): CellRange[] => {
    const byRow = new Map<number, number[]>();
    cells.forEach(([r, c]) => {
        const cols = byRow.get(r);
        if (cols) cols.push(c);
        else byRow.set(r, [c]);
    });
    const rects: CellRange[] = [];
    let above: CellRange[] = [];
    Array.from(byRow.keys())
        .sort((a, b) => a - b)
        .forEach((r) => {
            const runs: CellRange[] = [];
            byRow
                .get(r)!
                .sort((a, b) => a - b)
                .forEach((c) => {
                    const run = runs[runs.length - 1];
                    if (run && c === run.column[1] + 1) run.column[1] = c;
                    else if (!run || c !== run.column[1]) runs.push({ row: [r, r], column: [c, c] });
                });
            above = runs.map((run) => {
                const joined = above.find(
                    (rect) => rect.row[1] === r - 1 && rect.column[0] === run.column[0] && rect.column[1] === run.column[1],
                );
                if (joined) {
                    joined.row[1] = r;
                    return joined;
                }
                rects.push(run);
                return run;
            });
        });
    return rects;
};

// Excel string literal in a formula: wrapped in quotes, inner quotes doubled.
const quote = (s: string): string => `"${s.replace(/"/g, '""')}"`;
const unquote = (formula: string): string | undefined => {
    const m = /^"((?:[^"]|"")*)"$/.exec(formula.trim());
    return m ? m[1].replace(/""/g, '"') : undefined;
};

const DECIMAL_LITERAL = /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/;

// ── Colours ─────────────────────────────────────────────────────────────────
// Fortune stores CF colours as '#rrggbb' (its picker) or 'rgb(r, g, b)' (colour
// scales — its painter parses those with a hard-coded rgb() split).

const toHex2 = (n: number): string =>
    Math.max(0, Math.min(255, Math.round(n)))
        .toString(16)
        .padStart(2, '0');

const cssToArgb = (color: unknown): string | undefined => {
    if (typeof color !== 'string') return undefined;
    const s = color.trim();
    const hex6 = /^#([0-9a-f]{6})$/i.exec(s);
    if (hex6) return `FF${hex6[1].toUpperCase()}`;
    const hex3 = /^#([0-9a-f]{3})$/i.exec(s);
    if (hex3) return `FF${hex3[1].replace(/(.)/g, '$1$1').toUpperCase()}`;
    const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(s);
    if (rgb) return `FF${toHex2(Number(rgb[1]))}${toHex2(Number(rgb[2]))}${toHex2(Number(rgb[3]))}`.toUpperCase();
    return undefined;
};

const hexToRgbCss = (hex: string): string =>
    `rgb(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)})`;

// ============================================================================
// 1. Frozen panes
// ============================================================================

const FROZEN_ROW_TYPES = new Set(['row', 'both', 'rangeRow', 'rangeBoth']);
const FROZEN_COL_TYPES = new Set(['column', 'both', 'rangeColumn', 'rangeBoth']);

/** Fortune freezes rows 0..row_focus and columns 0..column_focus inclusive, for
 *  every type (core frozenTofreezen); with no range the focus defaults to 0, so
 *  'row'/'column'/'both' freeze the first row/column. Excel counts the frozen
 *  rows/columns instead (ySplit/xSplit). */
export const writeFrozen = (ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    const { frozen } = sheet;
    if (!frozen) return;
    const focus = (n: unknown): number => (isIndex(n) ? n : 0);
    const ySplit = FROZEN_ROW_TYPES.has(frozen.type) ? focus(frozen.range?.row_focus) + 1 : 0;
    const xSplit = FROZEN_COL_TYPES.has(frozen.type) ? focus(frozen.range?.column_focus) + 1 : 0;
    if (!xSplit && !ySplit) return;
    ws.views = [{ state: 'frozen', xSplit, ySplit }];
};

/** Hidden gridlines (Excel's View > Gridlines off): report-style sheets draw their own
 *  frame and read as a printed page. Written onto the first view, after any frozen pane. */
export const writeGridlines = (ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    if (sheet.showGridLines !== 0 && sheet.showGridLines !== false) return;
    const views = ws.views?.length ? ws.views : [{}];
    ws.views = views.map((view, i) => (i === 0 ? { ...view, showGridLines: false } : view)) as ExcelJS.Worksheet['views'];
};

export const readGridlines = (ws: ExcelJS.Worksheet): FortuneSheet['showGridLines'] =>
    (ws.views ?? []).some((view) => view?.showGridLines === false) ? 0 : undefined;

/** A 'split' (non-frozen, draggable) pane has no Fortune equivalent and is skipped. */
export const readFrozen = (ws: ExcelJS.Worksheet): FortuneSheet['frozen'] => {
    const view = (ws.views ?? []).find((v) => v?.state === 'frozen') as ExcelJS.WorksheetViewFrozen | undefined;
    if (!view) return undefined;
    const x = isIndex(view.xSplit) ? view.xSplit : 0;
    const y = isIndex(view.ySplit) ? view.ySplit : 0;
    if (x && y) return { type: 'rangeBoth', range: { row_focus: y - 1, column_focus: x - 1 } };
    if (y) return { type: 'rangeRow', range: { row_focus: y - 1, column_focus: 0 } };
    if (x) return { type: 'rangeColumn', range: { row_focus: 0, column_focus: x - 1 } };
    return undefined;
};

// ============================================================================
// 2. Hidden sheets
// ============================================================================

export const isHiddenSheetState = (state?: string): boolean => state === 'hidden' || state === 'veryHidden';

/** Excel refuses a workbook whose every sheet is hidden, and Fortune needs a
 *  visible tab to show — so in that case un-hide `fallback`. */
export const keepOneVisible = (hidden: boolean[], fallback = 0): boolean[] => {
    if (!hidden.length || !hidden.every(Boolean)) return hidden;
    const keep = Math.min(Math.max(fallback, 0), hidden.length - 1);
    return hidden.map((_, i) => i !== keep);
};

// ============================================================================
// 3. AutoFilter range
// ============================================================================

/** Writes the filter RANGE only, not Fortune's per-column criteria (sheet.filter).
 *  The export deliberately un-hides filter-hidden rows (see filterHiddenRows in
 *  converters.ts), so the reopened sheet shows every row with the filter
 *  dropdowns back in place, rather than a filter whose hidden rows were lost. */
export const writeAutoFilter = (ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    const range = toCellRange(sheet.filter_select);
    if (!range) return;
    ws.autoFilter = {
        from: { row: range.row[0] + 1, column: range.column[0] + 1 },
        to: { row: range.row[1] + 1, column: range.column[1] + 1 },
    };
};

export const readAutoFilter = (ws: ExcelJS.Worksheet, extent: GridExtent): FortuneSheet['filter_select'] => {
    const af = ws.autoFilter;
    if (!af) return undefined;
    const addr = (a: string | { row: number; column: number }): string =>
        typeof a === 'string' ? a : XLSX.utils.encode_cell({ r: a.row - 1, c: a.column - 1 });
    return parseRef(typeof af === 'string' ? af : `${addr(af.from)}:${addr(af.to)}`, extent);
};

// ============================================================================
// 4. Conditional formatting
// ============================================================================
// Fortune rule (core setConditionRules / compute):
//   { type: 'default', cellrange: [{row, column}], format: {textColor, cellColor},
//     conditionName, conditionRange: [], conditionValue: [...] }
//   { type: 'dataBar', cellrange, format: [color, gradientEndColor?] }
//   { type: 'colorGradation', cellrange, format: [max, mid, min] | [max, min] }
//
// Fortune ⇄ Excel:
//   greaterThan / lessThan / equal / between ⇄ cellIs (literal operands only)
//   textContains     ⇄ containsText (SEARCH formula — Excel matches case-insensitively)
//   duplicateValue   → expression COUNTIF(range,cell)>1 ('0') / =1 ('1') — exceljs
//                      can't write Excel's duplicateValues type; on import that
//                      COUNTIF shape (counting the rule's own range from its first
//                      cell — over another range it is a different rule) and native
//                      duplicateValues/uniqueValues map back
//   top10 / top10_percent / last10 / last10_percent ⇄ top10 (rank/percent/bottom)
//   aboveAverage / belowAverage ⇄ aboveAverage
//   formula          → expression (save only: Fortune evaluates a formula rule while
//                      painting, writing into the frozen sheet state, which takes the
//                      grid down — so expression rules are not imported)
//   colorGradation   ⇄ colorScale (min/[percentile 50]/max; Fortune's 3-colour
//                      midpoint is the average, so the middle stop is approximate)
//   dataBar          ⇄ dataBar (min/max)
// A range past the grid is clamped on import (parseRef); the file's own range is
// written back while the rule is unchanged (`source`, kept by readConditionalFormats).
// Skipped: Fortune occurrenceDate (absolute dates; Excel's timePeriod is relative
// to today) and icons (Fortune never paints them); Excel iconSet, timePeriod,
// containsBlanks/notContainsBlanks/containsErrors/notContainsErrors,
// notContainsText/beginsWith/endsWith, cellIs notBetween/notEqual/
// greaterThanOrEqual/lessThanOrEqual (no Fortune operator), cellIs with a cell
// reference or formula operand, and colour scales / data bars whose thresholds
// aren't min…max.

type FortuneCfRule = {
    type?: string;
    cellrange?: unknown[];
    format?: unknown;
    conditionName?: string;
    conditionValue?: unknown[];
    /** Set on import when the file's range reached past the grid: that range, and the clamped one it became. */
    source?: { ref: string; cellrange: CellRange[] };
};
type ExcelCfEntry = { ref: string; rule: Record<string, unknown> };

const CELL_IS_NAMES = new Set(['greaterThan', 'lessThan', 'equal', 'between']);
const TOP_N: Record<string, { bottom: boolean; percent: boolean }> = {
    top10: { bottom: false, percent: false },
    top10_percent: { bottom: false, percent: true },
    last10: { bottom: true, percent: false },
    last10_percent: { bottom: true, percent: true },
};

const highlightStyle = (format: unknown): Partial<ExcelJS.Style> => {
    const { textColor, cellColor } = (format ?? {}) as { textColor?: unknown; cellColor?: unknown };
    const style: Partial<ExcelJS.Style> = {};
    const font = cssToArgb(textColor);
    if (font) style.font = { color: { argb: font } };
    // Excel paints a conditional solid fill from bgColor (not fgColor as in cell styles).
    const fill = cssToArgb(cellColor);
    if (fill) style.fill = { type: 'pattern', pattern: 'solid', bgColor: { argb: fill } };
    return style;
};

// Fortune compares against the raw operand; numbers go out verbatim, anything else as a string literal.
const operandFormula = (v: unknown): string | undefined => {
    if (v == null || v === '') return undefined;
    const s = String(v);
    return DECIMAL_LITERAL.test(s) ? s.trim() : quote(s);
};

/** The rule's ranges to write: the file's own while the rule is unchanged since import (readConditionalFormats). */
const writtenRanges = (rule: FortuneCfRule): CellRange[] => {
    const current = (rule.cellrange ?? []).map(toCellRange).filter((r): r is CellRange => !!r);
    return rule.source && sameRanges(current, rule.source.cellrange) ? parseRefs(rule.source.ref, FULL_EXTENT) : current;
};

const toExcelCf = (rule: FortuneCfRule): ExcelCfEntry[] => {
    const ranges = writtenRanges(rule);
    if (!ranges.length) return [];
    const ref = ranges.map(encodeRange).join(' ');
    const tl = XLSX.utils.encode_cell({ r: ranges[0].row[0], c: ranges[0].column[0] });
    const one = (r: Record<string, unknown>): ExcelCfEntry[] => [{ ref, rule: r }];

    if (rule.type === 'dataBar') {
        const colors = Array.isArray(rule.format) ? rule.format : [];
        const argb = cssToArgb(colors[0]);
        return argb ? one({ type: 'dataBar', cfvo: [{ type: 'min' }, { type: 'max' }], color: { argb }, gradient: colors.length > 1 }) : [];
    }
    if (rule.type === 'colorGradation') {
        // Fortune lists colours max-first; Excel pairs them with its cfvo stops, min-first.
        const colors = (Array.isArray(rule.format) ? rule.format : []).map(cssToArgb);
        if ((colors.length !== 2 && colors.length !== 3) || colors.some((c) => !c)) return [];
        const cfvo =
            colors.length === 3
                ? [{ type: 'min' }, { type: 'percentile', value: 50 }, { type: 'max' }]
                : [{ type: 'min' }, { type: 'max' }];
        return one({ type: 'colorScale', cfvo, color: [...colors].reverse().map((argb) => ({ argb })) });
    }
    if (rule.type !== 'default') return [];

    const style = highlightStyle(rule.format);
    const name = rule.conditionName ?? '';
    const [v1, v2] = rule.conditionValue ?? [];
    if (CELL_IS_NAMES.has(name)) {
        const f1 = operandFormula(v1);
        const f2 = operandFormula(v2);
        if (!f1 || (name === 'between' && !f2)) return [];
        return one({ type: 'cellIs', operator: name, formulae: name === 'between' ? [f1, f2] : [f1], style });
    }
    if (name === 'textContains') {
        if (v1 == null || v1 === '') return [];
        // exceljs would build this formula itself but without escaping quotes in the text.
        const text = String(v1);
        return one({
            type: 'containsText',
            operator: 'containsText',
            text,
            formulae: [`NOT(ISERROR(SEARCH(${quote(text)},${tl})))`],
            style,
        });
    }
    if (name === 'duplicateValue') {
        // Fortune finds duplicates within each range separately, so each range gets its own COUNTIF.
        const test = String(v1) === '1' ? '=1' : '>1';
        return ranges.map((range) => {
            const abs = encodeRange(range).replace(/([A-Z]+)(\d+)/g, '$$$1$$$2');
            const cell = XLSX.utils.encode_cell({ r: range.row[0], c: range.column[0] });
            return { ref: encodeRange(range), rule: { type: 'expression', formulae: [`COUNTIF(${abs},${cell})${test}`], style } };
        });
    }
    if (TOP_N[name]) {
        const rank = Number(v1);
        return Number.isInteger(rank) && rank >= 1 ? one({ type: 'top10', rank, ...TOP_N[name], style }) : [];
    }
    if (name === 'aboveAverage' || name === 'belowAverage')
        return one({ type: 'aboveAverage', aboveAverage: name === 'aboveAverage', style });
    if (name === 'formula') {
        const formula = String(v1 ?? '').replace(/^=/, '');
        return formula ? one({ type: 'expression', formulae: [formula], style }) : [];
    }
    return [];
};

export const writeConditionalFormats = (ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    const rules = sheet.luckysheet_conditionformat_save;
    if (!Array.isArray(rules) || !rules.length) return;
    // Fortune paints its rules in order, a later one over an earlier one; in
    // Excel the lowest priority number wins — so the last rule gets priority 1.
    let priority = 1;
    for (let i = rules.length - 1; i >= 0; i--) {
        try {
            toExcelCf(rules[i] as FortuneCfRule).forEach(({ ref, rule }) => {
                ws.addConditionalFormatting({ ref, rules: [{ ...rule, priority } as unknown as ExcelJS.ConditionalFormattingRule] });
                priority += 1;
            });
        } catch {
            // Malformed rule: drop it, keep the rest.
        }
    }
};

// A cellIs operand Fortune can compare against: a number or a string literal (not a reference/expression).
const literalOperand = (formula: unknown): string | undefined => {
    if (typeof formula === 'number') return Number.isFinite(formula) ? String(formula) : undefined;
    if (typeof formula !== 'string') return undefined;
    if (DECIMAL_LITERAL.test(formula)) return formula.trim();
    return unquote(formula);
};

// COUNTIF(<range or whole column>, <cell>) > 1 | = 1 — the counted range, the cell and the test.
const DUPLICATE_FORMULA =
    /^COUNTIF\(\s*(\$?[A-Z]{1,3}(?:\$?\d+)?(?::\$?[A-Z]{1,3}(?:\$?\d+)?)?)\s*,\s*(\$?[A-Z]{1,3}\$?\d+)\s*\)\s*(>\s*1|=\s*1)$/i;
const SEARCH_TEXT = /SEARCH\(\s*"((?:[^"]|"")*)"\s*,/i;

const fromExcelCf = (rule: ExcelCfRule, cellrange: CellRange[], extent: GridExtent, resolve: ColorResolver): FortuneCfRule | undefined => {
    const format = {
        textColor: resolve(rule.style?.font?.color ?? undefined) ?? null,
        cellColor: resolve(rule.style?.fill?.bgColor ?? rule.style?.fill?.fgColor ?? undefined) ?? null,
    };
    const highlight = (conditionName: string, conditionValue: unknown[]): FortuneCfRule & { conditionRange: unknown[] } => ({
        type: 'default',
        cellrange,
        format,
        conditionName,
        conditionRange: [],
        conditionValue,
    });
    const formulae = rule.formulae ?? [];

    switch (rule.type) {
        case 'cellIs': {
            if (!rule.operator || !CELL_IS_NAMES.has(rule.operator)) return undefined;
            const count = rule.operator === 'between' ? 2 : 1;
            const values = formulae.slice(0, count).map(literalOperand);
            return values.length === count && values.every((v) => v !== undefined) ? highlight(rule.operator, values) : undefined;
        }
        case 'containsText': {
            if (rule.operator !== 'containsText') return undefined;
            const searched = SEARCH_TEXT.exec(String(formulae[0] ?? ''));
            const text = rule.text ?? (searched ? searched[1].replace(/""/g, '"') : undefined);
            return text ? highlight('textContains', [text]) : undefined;
        }
        case 'duplicateValues':
        case 'uniqueValues':
            return highlight('duplicateValue', [rule.type === 'uniqueValues' ? '1' : '0']);
        case 'expression': {
            // Only the duplicates shape, and only when it counts the rule's own range from its first
            // cell (over another range it is a different rule). Any other formula rule is skipped:
            // Fortune evaluates it while painting, writing into the frozen sheet state, which takes
            // the grid down.
            const dup = DUPLICATE_FORMULA.exec(String(formulae[0] ?? '').trim());
            if (!dup || cellrange.length !== 1) return undefined;
            const counted = parseRef(dup[1], extent);
            const cell = parseRef(dup[2], extent);
            const [own] = cellrange;
            if (!counted || !cell || !sameRange(counted, own) || cell.row[0] !== own.row[0] || cell.column[0] !== own.column[0])
                return undefined;
            return highlight('duplicateValue', [dup[3].replace(/\s/g, '') === '=1' ? '1' : '0']);
        }
        case 'top10': {
            const rank = rule.rank ?? 10; // Excel's default rank
            if (!Number.isInteger(rank) || rank < 1) return undefined;
            const name = `${rule.bottom ? 'last10' : 'top10'}${rule.percent ? '_percent' : ''}`;
            return highlight(name, [String(rank)]);
        }
        case 'aboveAverage':
            return highlight(rule.aboveAverage === false ? 'belowAverage' : 'aboveAverage', [
                rule.aboveAverage === false ? 'belowAverage' : 'aboveAverage',
            ]);
        case 'colorScale': {
            const cfvo = rule.cfvo ?? [];
            const colors = (Array.isArray(rule.color) ? rule.color : []).map((c) => resolve(c));
            if ((colors.length !== 2 && colors.length !== 3) || cfvo.length !== colors.length) return undefined;
            if (cfvo[0]?.type !== 'min' || cfvo[cfvo.length - 1]?.type !== 'max' || colors.some((c) => !c)) return undefined;
            return { type: 'colorGradation', cellrange, format: (colors as string[]).reverse().map(hexToRgbCss) };
        }
        case 'dataBar': {
            const color = resolve(Array.isArray(rule.color) ? rule.color[0] : rule.color);
            const minMax = (rule.cfvo ?? []).every((c) => ['min', 'max', 'autoMin', 'autoMax'].includes(c?.type ?? ''));
            if (!color || !minMax) return undefined;
            // Excel's bars are gradient unless told otherwise; Fortune draws a gradient for a 2-colour format.
            return { type: 'dataBar', cellrange, format: rule.gradient === false ? [color] : [color, '#ffffff'] };
        }
        default:
            return undefined;
    }
};

export const readConditionalFormats = (ws: ExcelJS.Worksheet, extent: GridExtent, resolve: ColorResolver): FortuneCfRule[] | undefined => {
    const found: { priority: number; rule: FortuneCfRule }[] = [];
    (internals(ws).conditionalFormattings ?? []).forEach((block) => {
        try {
            const ref = String(block?.ref ?? '');
            const ranges = parseRefs(ref, extent);
            if (!ranges.length) return;
            const clamped = !sameRanges(ranges, parseRefs(ref, FULL_EXTENT));
            (block.rules ?? []).forEach((rule) => {
                try {
                    const mapped = rule && fromExcelCf(rule, ranges, extent, resolve);
                    if (!mapped) return;
                    // A copy: the rule's own cellrange moves with the user's edits, this stays what the import made.
                    if (clamped) mapped.source = { ref, cellrange: ranges.map(copyRange) };
                    found.push({ priority: rule.priority ?? 0, rule: mapped });
                } catch {
                    // Malformed rule: drop it, keep the rest.
                }
            });
        } catch {
            // Malformed block: drop it, keep the rest.
        }
    });
    // Back to Fortune's paint order: the highest-precedence (lowest priority) rule last.
    found.sort((a, b) => b.priority - a.priority);
    return found.length ? found.map((f) => f.rule) : undefined;
};

// ============================================================================
// 5. Data validation
// ============================================================================
// Fortune (core validateCellData) → Excel:
//   dropdown          → list "a,b,c" (or the range reference it still holds);
//                       multi-select (type2) has no Excel equivalent and is dropped
//   checkbox          → list of its own checked/unchecked values ("value1,value2"):
//                       Excel has no checkbox validation, and the cell holds one of
//                       the two — it reopens as a Fortune dropdown
//   number            ⇄ decimal
//   number_decimal    → decimal (Excel also accepts whole numbers there)
//   number_integer    ⇄ whole
//   text_length       ⇄ textLength
//   date              ⇄ date
//   text_content      ⇄ custom ISNUMBER(FIND(..)) / ISERROR(FIND(..)) / EXACT(..)
//   validity          → skipped (Chinese ID-card / phone-number checks)
// prohibitInput ⇄ showErrorMessage (stop style); hintShow/hintValue ⇄ showInputMessage/prompt.
// Any other Excel custom formula, and list/number/date rules whose operands are
// references or formulas, are skipped on import. Excel's error message, style,
// blank rule and prompt title have no Fortune field: they ride on the item from
// the import and go back out on save, as does a range that reached past the grid.

// Settings Fortune has no field or UI for, kept on the item so a save writes them back.
const EXCEL_ONLY_VALIDATION_FIELDS = ['allowBlank', 'errorStyle', 'errorTitle', 'error', 'promptTitle'] as const;
type ExcelOnlyValidationFields = Pick<ExcelJS.DataValidation, (typeof EXCEL_ONLY_VALIDATION_FIELDS)[number]>;
// Excel's own defaults are not carried (a blank is allowed unless the rule says otherwise).
const VALIDATION_DEFAULTS: ExcelOnlyValidationFields = { allowBlank: true };
const excelOnlyFields = (from: ExcelOnlyValidationFields): ExcelOnlyValidationFields =>
    Object.fromEntries(
        EXCEL_ONLY_VALIDATION_FIELDS.filter((key) => from[key] != null && from[key] !== VALIDATION_DEFAULTS[key]).map((key) => [
            key,
            from[key],
        ]),
    );

type FortuneValidation = ExcelOnlyValidationFields & {
    type?: string;
    type2?: string;
    value1?: unknown;
    value2?: unknown;
    prohibitInput?: boolean;
    hintShow?: boolean;
    hintValue?: unknown;
    /** Set on import when the rule reached past the grid: its rectangles in the file, and within the grid. */
    source?: { rects: CellRange[]; gridRects: CellRange[] };
    [key: string]: unknown;
};

/** A clamped rule's rectangles within the grid, and in the file: the grid ones extended by the rows past it
 *  (`tail`: per column, runs of rows), the rest of the tail as rectangles of its own. */
const importedRects = (grid: [number, number][], tail: Map<number, [number, number][]>): NonNullable<FortuneValidation['source']> => {
    const gridRects = rectanglesOf(grid);
    const rects = gridRects.map(copyRange);
    const tailRects: CellRange[] = [];
    Array.from(tail.keys())
        .sort((a, b) => a - b)
        .forEach((c) =>
            tail.get(c)!.forEach(([r1, r2]) => {
                const left = tailRects.find((rect) => rect.column[1] === c - 1 && rect.row[0] === r1 && rect.row[1] === r2);
                if (left) left.column[1] = c;
                else tailRects.push({ row: [r1, r2], column: [c, c] });
            }),
        );
    tailRects.forEach((tailRect) => {
        const above = rects.find(
            (rect) => rect.row[1] === tailRect.row[0] - 1 && rect.column[0] === tailRect.column[0] && rect.column[1] === tailRect.column[1],
        );
        if (above) above.row[1] = tailRect.row[1];
        else rects.push(tailRect);
    });
    return { gridRects, rects };
};

const NUMBER_OPERATORS: Record<string, ExcelJS.DataValidationOperator> = {
    between: 'between',
    notBetween: 'notBetween',
    equal: 'equal',
    notEqualTo: 'notEqual',
    moreThanThe: 'greaterThan',
    lessThan: 'lessThan',
    greaterOrEqualTo: 'greaterThanOrEqual',
    lessThanOrEqualTo: 'lessThanOrEqual',
};
const DATE_OPERATORS: Record<string, ExcelJS.DataValidationOperator> = {
    between: 'between',
    notBetween: 'notBetween',
    equal: 'equal',
    notEqualTo: 'notEqual',
    earlierThan: 'lessThan',
    noEarlierThan: 'greaterThanOrEqual',
    laterThan: 'greaterThan',
    noLaterThan: 'lessThanOrEqual',
};
const invert = (map: Record<string, string>): Record<string, string> => Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
const NUMBER_OPERATORS_IN = invert(NUMBER_OPERATORS);
const DATE_OPERATORS_IN = invert(DATE_OPERATORS);
const NUMERIC_TYPES: Record<string, 'decimal' | 'whole' | 'textLength'> = {
    number: 'decimal',
    number_decimal: 'decimal',
    number_integer: 'whole',
    text_length: 'textLength',
};
const NUMERIC_TYPES_IN: Record<string, string> = { decimal: 'number', whole: 'number_integer', textLength: 'text_length' };

const RANGE_REFERENCE = /^(?:(?:'[^']+'|[^'!,"]+)!)?\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?$/i;
// Excel caps an inline list at 255 characters and reports the file as corrupt past it.
const MAX_INLINE_LIST = 255;

const listFormula = (value: unknown): string | undefined => {
    const s = value == null ? '' : String(value).trim();
    if (!s) return undefined;
    if (RANGE_REFERENCE.test(s)) return s;
    const items = s
        .split(',')
        .filter((item) => item !== '')
        .join(',');
    return items && items.length <= MAX_INLINE_LIST ? quote(items) : undefined;
};

const parseDate = (value: unknown): Date | undefined => {
    const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(String(value ?? '').trim());
    if (!m) return undefined;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return Number.isNaN(d.getTime()) ? undefined : d;
};
const formatDate = (value: unknown): string | undefined => {
    const d = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
};

const TEXT_CONTENT_FORMULAS: Record<string, (text: string, cell: string) => string> = {
    include: (text, cell) => `ISNUMBER(FIND(${quote(text)},${cell}))`,
    exclude: (text, cell) => `ISERROR(FIND(${quote(text)},${cell}))`,
    equal: (text, cell) => `EXACT(${cell},${quote(text)})`,
};
const STR = '"((?:[^"]|"")*)"';
const CELL = '\\$?[A-Z]{1,3}\\$?\\d+';
const TEXT_CONTENT_PATTERNS: [string, RegExp][] = [
    ['include', new RegExp(`^ISNUMBER\\(FIND\\(${STR},${CELL}\\)\\)$`, 'i')],
    ['exclude', new RegExp(`^ISERROR\\(FIND\\(${STR},${CELL}\\)\\)$`, 'i')],
    ['equal', new RegExp(`^EXACT\\(${CELL},${STR}\\)$`, 'i')],
];

/** `topLeft` anchors a custom formula: Excel re-relativises it for every other cell of the range. */
const toExcelValidation = (item: FortuneValidation, topLeft: string): ExcelJS.DataValidation | undefined => {
    const hint = item.hintShow && item.hintValue ? String(item.hintValue).slice(0, MAX_INLINE_LIST) : '';
    const common = {
        allowBlank: true,
        showErrorMessage: !!item.prohibitInput || item.errorStyle != null,
        ...(hint ? { showInputMessage: true, prompt: hint } : {}),
        ...excelOnlyFields(item),
    };
    const { type, type2 } = item;
    if (type === 'dropdown' || type === 'checkbox') {
        const formula =
            type === 'dropdown'
                ? listFormula(item.value1)
                : listFormula([item.value1, item.value2].map((v) => (v == null ? '' : String(v))).join(','));
        if (!formula || (type === 'checkbox' && (!item.value1 || !item.value2))) return undefined;
        return { ...common, type: 'list', formulae: [formula] };
    }
    if (type && NUMERIC_TYPES[type]) {
        const operator = type2 ? NUMBER_OPERATORS[type2] : undefined;
        if (!operator) return undefined;
        const raw = operator === 'between' || operator === 'notBetween' ? [item.value1, item.value2] : [item.value1];
        const values = raw.map((v) => (v === '' || v == null ? NaN : Number(v)));
        if (values.some((v) => !Number.isFinite(v))) return undefined;
        return { ...common, type: NUMERIC_TYPES[type], operator, formulae: values };
    }
    if (type === 'date') {
        const operator = type2 ? DATE_OPERATORS[type2] : undefined;
        if (!operator) return undefined;
        const dates = (operator === 'between' || operator === 'notBetween' ? [item.value1, item.value2] : [item.value1]).map(parseDate);
        if (dates.some((d) => !d)) return undefined;
        return { ...common, type: 'date', operator, formulae: dates };
    }
    if (type === 'text_content') {
        const build = type2 ? TEXT_CONTENT_FORMULAS[type2] : undefined;
        if (!build || item.value1 == null || item.value1 === '') return undefined;
        return { ...common, type: 'custom', formulae: [build(String(item.value1), topLeft)] };
    }
    return undefined;
};

export const writeDataValidations = (ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    const all = sheet.dataVerification as Record<string, unknown> | undefined;
    const store = internals(ws).dataValidations;
    if (!all || typeof all !== 'object' || !store) return;

    // Fortune keeps one entry per cell (usually the same object for a whole
    // range); group equal entries so a range goes out as one Excel rule.
    const groups = new Map<string, { item: FortuneValidation; cells: [number, number][] }>();
    const groupKeys = new Map<object, string>();
    Object.keys(all).forEach((key) => {
        const item = all[key];
        const m = /^(\d+)_(\d+)$/.exec(key);
        if (!m || !item || typeof item !== 'object') return;
        let groupKey = groupKeys.get(item);
        if (groupKey === undefined) {
            groupKey = JSON.stringify(item);
            groupKeys.set(item, groupKey);
        }
        const group = groups.get(groupKey) ?? { item: item as FortuneValidation, cells: [] };
        group.cells.push([Number(m[1]), Number(m[2])]);
        groups.set(groupKey, group);
    });

    groups.forEach(({ item, cells }) => {
        try {
            const rects = rectanglesOf(cells);
            // The file's own ranges while the rule still covers what it did on import (readDataValidations).
            const written = item.source && sameRanges(rects, item.source.gridRects) ? item.source.rects : rects;
            written.forEach((rect) => {
                const validation = toExcelValidation(item, XLSX.utils.encode_cell({ r: rect.row[0], c: rect.column[0] }));
                if (validation) store.add(encodeRange(rect), validation);
            });
        } catch {
            // Malformed validation: drop it, keep the rest.
        }
    });
};

const fromExcelValidation = (dv: ExcelJS.DataValidation): FortuneValidation | undefined => {
    const formulae = dv.formulae ?? [];
    const base = {
        type2: '',
        value1: '',
        value2: '',
        validity: '',
        remote: false,
        prohibitInput: dv.showErrorMessage === true && (!dv.errorStyle || dv.errorStyle === 'stop'),
        hintShow: !!(dv.showInputMessage && dv.prompt),
        hintValue: dv.prompt ?? '',
        checked: false,
        // exceljs leaves `allowBlank` undefined when the file has no attribute, which to Excel means no blanks.
        ...excelOnlyFields({ ...dv, allowBlank: dv.allowBlank === true }),
    };
    if (dv.type === 'list') {
        const formula = String(formulae[0] ?? '').trim();
        const list = unquote(formula);
        if (list !== undefined) return list ? { ...base, type: 'dropdown', value1: list } : undefined;
        // Kept as written, `$` included: Fortune reads either form, and a save writes it back as it was
        // (Excel reads an unanchored reference relative to each cell of the rule).
        const ref = formula.replace(/^=/, '');
        return RANGE_REFERENCE.test(ref) ? { ...base, type: 'dropdown', value1: ref } : undefined;
    }
    if (NUMERIC_TYPES_IN[dv.type]) {
        const type2 = NUMBER_OPERATORS_IN[dv.operator ?? 'between'];
        const count = type2 === 'between' || type2 === 'notBetween' ? 2 : 1;
        const values = formulae.slice(0, count).map(Number);
        if (!type2 || values.length !== count || values.some((v) => !Number.isFinite(v))) return undefined;
        return { ...base, type: NUMERIC_TYPES_IN[dv.type], type2, value1: String(values[0]), value2: count === 2 ? String(values[1]) : '' };
    }
    if (dv.type === 'date') {
        const type2 = DATE_OPERATORS_IN[dv.operator ?? 'between'];
        const count = type2 === 'between' || type2 === 'notBetween' ? 2 : 1;
        const dates = formulae.slice(0, count).map(formatDate);
        if (!type2 || dates.length !== count || dates.some((d) => !d)) return undefined;
        return { ...base, type: 'date', type2, value1: dates[0], value2: count === 2 ? dates[1] : '' };
    }
    if (dv.type === 'custom') {
        const formula = String(formulae[0] ?? '')
            .trim()
            .replace(/^=/, '');
        for (const [type2, pattern] of TEXT_CONTENT_PATTERNS) {
            const m = pattern.exec(formula);
            if (m) return { ...base, type: 'text_content', type2, value1: m[1].replace(/""/g, '"') };
        }
    }
    return undefined;
};

export const readDataValidations = (ws: ExcelJS.Worksheet, extent: GridExtent): Record<string, FortuneValidation> | undefined => {
    const model = internals(ws).dataValidations?.model;
    if (!model) return undefined;
    const out: Record<string, FortuneValidation> = {};
    // exceljs expands each rule to every address of its range but shares one
    // object between them — convert each rule once and share the result too.
    const converted = new Map<object, FortuneValidation | null>();
    // Where each rule lands: its cells within the grid, and the rows past it per column as runs
    // (exceljs expands a range row by row, so a column's rows arrive in order).
    const placed = new Map<FortuneValidation, { grid: [number, number][]; tail: Map<number, [number, number][]> }>();
    Object.keys(model).forEach((address) => {
        const dv = model[address];
        if (!dv || typeof dv !== 'object') return;
        let item = converted.get(dv);
        if (item === undefined) {
            try {
                item = fromExcelValidation(dv) ?? null;
            } catch {
                item = null;
            }
            converted.set(dv, item);
        }
        if (!item) return;
        const { r, c } = XLSX.utils.decode_cell(address.replace(/\$/g, ''));
        if (r < 0 || c < 0) return;
        let where = placed.get(item);
        if (!where) {
            where = { grid: [], tail: new Map() };
            placed.set(item, where);
        }
        // Clamped to the grid like CF ranges: a whole-column rule would mint a million keys.
        if (r < extent.rows && c < extent.cols) {
            out[`${r}_${c}`] = item;
            where.grid.push([r, c]);
            return;
        }
        const runs = where.tail.get(c) ?? [];
        const last = runs[runs.length - 1];
        if (last && r === last[1] + 1) last[1] = r;
        else runs.push([r, r]);
        where.tail.set(c, runs);
    });
    placed.forEach((where, item) => {
        if (where.tail.size) item.source = importedRects(where.grid, where.tail);
    });
    return Object.keys(out).length ? out : undefined;
};

// ============================================================================
// 6. Images
// ============================================================================
// Fortune positions an image in grid pixels (left/top from the grid origin);
// Excel anchors it to a cell plus an EMU offset. Fortune lays row/column i out
// at the sum of (size + 1px gridline) of every visible track before it, hidden
// ones taking no space (core calcRowColSize); the conversion walks the same
// geometry, so it's exact for the editor and approximate in Excel, whose
// default sizes and gridlines differ slightly.

const EMU_PER_PX = 9525;
// Excel's own row/column limits — bounds the walk on a pathological position.

type TrackSize = (i: number) => number;
const trackSizer =
    (lens: Record<string, number> | undefined, hidden: Record<string, number> | undefined, fallback: number): TrackSize =>
    (i) =>
        hidden?.[i] != null ? 0 : (lens?.[i] || fallback) + 1;

const pxToTrack = (px: number, size: TrackSize, max: number): { index: number; offset: number } => {
    let index = 0;
    let start = 0;
    while (index < max - 1 && start + size(index) <= px) {
        start += size(index);
        index += 1;
    }
    return { index, offset: Math.max(0, px - start) };
};

const trackToPx = (index: number, size: TrackSize, max: number): number => {
    let px = 0;
    for (let i = 0; i < Math.min(index, max); i++) px += size(i);
    return px;
};

const sheetTrackSizers = (sheet: FortuneSheet): { col: TrackSize; row: TrackSize } => ({
    col: trackSizer(sheet.config?.columnlen, sheet.config?.colhidden, sheet.defaultColWidth ?? DEFAULT_COL_WIDTH),
    row: trackSizer(sheet.config?.rowlen, sheet.config?.rowhidden, sheet.defaultRowHeight ?? MIN_ROW_HEIGHT),
});

const IMAGE_DATA_URL = /^data:image\/(png|jpe?g|gif);base64,/i;

/** Only embedded data-URL images (png/jpeg/gif) are written. A remote src would
 *  have to be fetched, which this DOM-free worker serialiser doesn't do — those
 *  are skipped. */
export const writeImages = (wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    const { images } = sheet;
    if (!Array.isArray(images) || !images.length) return;
    const size = sheetTrackSizers(sheet);
    images.forEach((image) => {
        try {
            const src = typeof image?.src === 'string' ? image.src : '';
            const m = IMAGE_DATA_URL.exec(src);
            const width = Number(image.width);
            const height = Number(image.height);
            if (!m || !(width > 0) || !(height > 0)) return;
            const ext = m[1].toLowerCase();
            const imageId = wb.addImage({
                base64: src.slice(m[0].length),
                extension: ext === 'jpg' ? 'jpeg' : (ext as 'png' | 'jpeg' | 'gif'),
            });
            const col = pxToTrack(Number(image.left) || 0, size.col, MAX_COLS);
            const row = pxToTrack(Number(image.top) || 0, size.row, MAX_ROWS);
            // A native anchor (cell + EMU offset): exceljs's fractional {col,row} form
            // scales the offset by its own made-up cell size, not the real one.
            const tl = {
                nativeCol: col.index,
                nativeColOff: Math.round(col.offset * EMU_PER_PX),
                nativeRow: row.index,
                nativeRowOff: Math.round(row.offset * EMU_PER_PX),
            };
            ws.addImage(imageId, { tl: tl as unknown as { col: number; row: number }, ext: { width, height }, editAs: 'oneCell' });
        } catch {
            // Malformed image: drop it, keep the rest.
        }
    });
};

const bytesToBase64 = (bytes: Uint8Array): string => {
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
    }
    return btoa(binary);
};

type ExcelAnchor = { nativeCol: number; nativeColOff: number; nativeRow: number; nativeRowOff: number };

export const readImages = (wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet, sheet: FortuneSheet): FortuneImage[] | undefined => {
    const placed = ws.getImages();
    if (!placed.length) return undefined;
    const size = sheetTrackSizers(sheet);
    const pos = (a: ExcelAnchor) => ({
        left: trackToPx(a.nativeCol, size.col, MAX_COLS) + (a.nativeColOff || 0) / EMU_PER_PX,
        top: trackToPx(a.nativeRow, size.row, MAX_ROWS) + (a.nativeRowOff || 0) / EMU_PER_PX,
    });
    const out: FortuneImage[] = [];
    placed.forEach((entry, i) => {
        try {
            const media = wb.getImage(Number(entry.imageId)) as { extension?: string; base64?: string; buffer?: Uint8Array } | undefined;
            const ext = String(media?.extension ?? '').toLowerCase();
            const mime = ext === 'jpg' ? 'jpeg' : ext;
            // EMF/WMF and the like have no browser rendering.
            if (!['png', 'jpeg', 'gif'].includes(mime)) return;
            const base64 = media?.base64 ? media.base64.replace(/^data:[^,]*,/, '') : media?.buffer ? bytesToBase64(media.buffer) : '';
            if (!base64) return;
            const {
                tl,
                br,
                ext: extent,
            } = entry.range as unknown as { tl: ExcelAnchor; br?: ExcelAnchor; ext?: { width?: number; height?: number } };
            const { left, top } = pos(tl);
            let width = extent?.width ?? 0;
            let height = extent?.height ?? 0;
            // A two-cell anchor carries no size of its own: measure tl → br.
            if (!(width > 0 && height > 0) && br) {
                const end = pos(br);
                width = end.left - left;
                height = end.top - top;
            }
            if (!(width > 0 && height > 0)) return;
            out.push({
                id: `img_${sheet.id ?? '0'}_${i}`,
                src: `data:image/${mime};base64,${base64}`,
                left: Math.round(left),
                top: Math.round(top),
                width: Math.round(width),
                height: Math.round(height),
            });
        } catch {
            // Malformed image: drop it, keep the rest.
        }
    });
    return out.length ? out : undefined;
};

// ============================================================================
// 7. Cell comments (notes)
// ============================================================================
// Fortune's comment value is HTML (its editor is contentEditable and saves
// innerHTML); an Excel note is plain text. Excel's "always show" flag isn't
// exposed by exceljs, so isShow doesn't round-trip (imports as hidden).

const decodeEntities = (s: string): string =>
    s
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');

const escapeHtml = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Fortune `ps` → Excel note text, or undefined for no/empty comment. */
export const commentToNote = (ps: unknown): string | undefined => {
    const value = (ps as { value?: unknown } | null | undefined)?.value;
    if (typeof value !== 'string' || !value) return undefined;
    // contentEditable starts each new line as a <div> (or <p>) and an empty one as <br>.
    const text = decodeEntities(value.replace(/<br\s*\/?>|<(div|p)(\s[^>]*)?>/gi, '\n').replace(/<[^>]*>/g, '')).replace(/^\n+|\n+$/g, '');
    return text || undefined;
};

/** exceljs `cell.note` (a string, or `{ texts: [{ text }] }` for formatted notes) → Fortune `ps`. */
export const noteToComment = (note: unknown): FortuneCell['ps'] | undefined => {
    if (note == null) return undefined;
    const texts = (note as { texts?: { text?: unknown }[] }).texts;
    const text =
        typeof note === 'string' ? note : Array.isArray(texts) ? texts.map((t) => (t?.text == null ? '' : String(t.text))).join('') : '';
    if (!text) return undefined;
    // Escaped because Fortune renders the value as HTML — note text is not markup.
    return { left: null, top: null, width: null, height: null, value: escapeHtml(text).replace(/\r?\n/g, '<br>'), isShow: false };
};

// ============================================================================
// 7. Notes on cells without a value
// ============================================================================
// exceljs attaches a comment only to a cell that has an element in the sheet's
// data, so a note on a blank cell never reaches `cell.note`. The comments parts
// are read again from the file, and a note whose cell the import did not emit
// becomes a comment-only cell.

const xmlAttr = (element: string, name: string): string | undefined => new RegExp(`\\b${name}="([^"]*)"`).exec(element)?.[1];
const xmlElements = (xml: string, tag: string): string[] => xml.match(new RegExp(`<${tag}\\b[^>]*/?>`, 'g')) ?? [];

/** The part a relationship target names, resolved against the directory of the part that holds the .rels. */
const relTarget = (dir: string, target: string): string => {
    const out: string[] = [];
    (target.startsWith('/') ? target : `${dir}/${target}`).split('/').forEach((part) => {
        if (part === '..') out.pop();
        else if (part && part !== '.') out.push(part);
    });
    return out.join('/');
};

/**
 * The worksheet XML parts `xl/workbook.xml` names, in workbook order — the order of exceljs's
 * wb.worksheets, which holds worksheets only: chart and dialog sheets are left out, or every later
 * worksheet would be paired with the previous sheet's part. Targets are resolved like any other
 * relationship (`./`, `../`, absolute), so the paths match the zip's entries.
 */
export const worksheetPartPaths = async (zip: JSZip): Promise<string[]> => {
    const read = (path: string) => zip.file(path)?.async('string');
    const workbook = (await read('xl/workbook.xml')) ?? '';
    const workbookRels = (await read('xl/_rels/workbook.xml.rels')) ?? '';
    const sheetParts = new Map(
        xmlElements(workbookRels, 'Relationship')
            .filter((rel) => /\/worksheet$/.test(xmlAttr(rel, 'Type') ?? ''))
            .map((rel) => [xmlAttr(rel, 'Id'), relTarget('xl', xmlAttr(rel, 'Target') ?? '')]),
    );
    return xmlElements(workbook, 'sheet')
        .map((sheet) => sheetParts.get(/\b(?:\w+:)?id="([^"]*)"/.exec(sheet)?.[1] ?? ''))
        .filter((part): part is string => Boolean(part));
};

/** Each worksheet's comments part (`xl/comments1.xml`), in worksheet order; undefined where a sheet has none. */
const commentsParts = async (zip: JSZip): Promise<(string | undefined)[]> => {
    const read = (path: string) => zip.file(path)?.async('string');
    return Promise.all(
        (await worksheetPartPaths(zip)).map(async (part) => {
            const dir = part.slice(0, part.lastIndexOf('/'));
            const sheetRels = (await read(`${dir}/_rels/${part.slice(dir.length + 1)}.rels`)) ?? '';
            const comments = xmlElements(sheetRels, 'Relationship').find((rel) => /\/comments$/.test(xmlAttr(rel, 'Type') ?? ''));
            return comments ? relTarget(dir, xmlAttr(comments, 'Target') ?? '') : undefined;
        }),
    );
};

/** Adds the notes of blank cells (see above) to `sheets`, which are in workbook order. */
export const addNotesOnBlankCells = async (buffer: ArrayBuffer, sheets: FortuneSheet[]): Promise<void> => {
    const zip = await JSZip.loadAsync(buffer);
    const parts = await commentsParts(zip);
    await Promise.all(
        sheets.map(async (sheet, i) => {
            const part = parts[i];
            const xml = part && (await zip.file(part)?.async('string'));
            if (!xml) return;
            const celldata = sheet.celldata ?? [];
            const present = new Set(celldata.map((cell) => `${cell.r}_${cell.c}`));
            const comment = /<comment\b([^>]*)>([\s\S]*?)<\/comment>/g;
            for (let m = comment.exec(xml); m; m = comment.exec(xml)) {
                const ref = xmlAttr(m[1], 'ref');
                const { r, c } = ref ? XLSX.utils.decode_cell(ref) : { r: -1, c: -1 };
                if (r < 0 || c < 0 || r >= (sheet.row ?? 0) || c >= (sheet.column ?? 0) || present.has(`${r}_${c}`)) continue;
                const text = Array.from(m[2].matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g), (run) => run[1]).join('');
                const ps = noteToComment(decodeEntities(text.replace(/&apos;/g, "'")));
                if (ps) celldata.push({ r, c, v: { ps } });
            }
            sheet.celldata = celldata;
        }),
    );
};

// ============================================================================
// Per-worksheet entry points (converters.ts)
// ============================================================================

// Each feature is independent: one that fails is dropped without costing the others.
const attempt = (what: string, run: () => void): void => {
    try {
        run();
    } catch (err) {
        console.warn(`[SheetEditor] skipped ${what} in the xlsx round trip`, err);
    }
};

/** Writes frozen panes, the AutoFilter range, CF, data validation and images. */
export const writeWorksheetFeatures = (wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet, sheet: FortuneSheet): void => {
    attempt('frozen panes', () => writeFrozen(ws, sheet));
    attempt('gridlines', () => writeGridlines(ws, sheet));
    attempt('autoFilter', () => writeAutoFilter(ws, sheet));
    attempt('conditional formatting', () => writeConditionalFormats(ws, sheet));
    attempt('data validation', () => writeDataValidations(ws, sheet));
    attempt('images', () => writeImages(wb, ws, sheet));
};

/** Reads the same features back onto a sheet built by converters.ts's buildSheet
 *  (its row/column extent and config are what ranges and images are measured against). */
export const readWorksheetFeatures = (
    wb: ExcelJS.Workbook,
    ws: ExcelJS.Worksheet,
    sheet: FortuneSheet,
    resolveColor: ColorResolver,
): Partial<FortuneSheet> => {
    const extent = sheetExtent(sheet);
    const out: Partial<FortuneSheet> = {};
    attempt('frozen panes', () => {
        out.frozen = readFrozen(ws);
    });
    attempt('gridlines', () => {
        out.showGridLines = readGridlines(ws);
    });
    attempt('autoFilter', () => {
        out.filter_select = readAutoFilter(ws, extent);
    });
    attempt('conditional formatting', () => {
        out.luckysheet_conditionformat_save = readConditionalFormats(ws, extent, resolveColor);
    });
    attempt('data validation', () => {
        out.dataVerification = readDataValidations(ws, extent);
    });
    attempt('images', () => {
        out.images = readImages(wb, ws, sheet);
    });
    (Object.keys(out) as (keyof FortuneSheet)[]).forEach((key) => {
        if (out[key] === undefined) delete out[key];
    });
    return out;
};
